import { Injectable, inject } from '@angular/core';
import { State, Selector, Action, StateContext } from '@ngxs/store';
import { EMPTY, Observable, finalize, forkJoin, tap } from 'rxjs';
import { INotification } from '../../interfaces/notifications/INotification';
import { NotificationClass } from '../../interfaces/notifications/NotificationClass';
import { UserNotificationsService } from '../../services/user-notifications.service';
import {
  DismissAllNotifications,
  DismissNotification,
  LoadPendingNotifications,
  LoadPersistentNotifications,
} from './notifications.actions';
import { NotificationsStateModel } from './notifications-state-model.interface';

@State<NotificationsStateModel>({
  name: 'notificationsState',
  defaults: {
    pendingNotifications: [],
    isLoading: false,
  },
})
@Injectable()
export class NotificationsState {
  private readonly notificationsService = inject(UserNotificationsService);

  @Selector()
  static pendingNotifications(state: NotificationsStateModel): INotification[] {
    return state.pendingNotifications;
  }

  @Selector()
  static isLoading(state: NotificationsStateModel): boolean {
    return state.isLoading;
  }

  @Action(LoadPendingNotifications)
  loadPendingNotifications(ctx: StateContext<NotificationsStateModel>): Observable<INotification[]> {
    ctx.patchState({ isLoading: true });
    return this.notificationsService.getPendingNotifications().pipe(
      tap(pendingNotifications => ctx.patchState({ pendingNotifications })),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(LoadPersistentNotifications)
  loadPersistentNotifications(ctx: StateContext<NotificationsStateModel>): Observable<INotification[]> {
    if (ctx.getState().isLoading) {
      return EMPTY;
    }

    ctx.patchState({ isLoading: true });
    return this.notificationsService.getPersistentNotifications().pipe(
      tap(persistentNotifications =>
        ctx.setState(state => ({
          ...state,
          pendingNotifications: [
            ...state.pendingNotifications.filter(notification => notification.class === NotificationClass.COMPUTED),
            ...persistentNotifications,
          ],
        })),
      ),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(DismissNotification)
  dismissNotification(ctx: StateContext<NotificationsStateModel>, action: DismissNotification): Observable<void> {
    if (ctx.getState().isLoading) {
      return EMPTY;
    }

    ctx.patchState({ isLoading: true });
    return this.notificationsService.dismissNotification(action.id).pipe(
      tap(() =>
        ctx.setState(state => ({
          ...state,
          pendingNotifications: state.pendingNotifications.filter(notification => notification.id !== action.id),
        })),
      ),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(DismissAllNotifications)
  dismissAllNotifications(ctx: StateContext<NotificationsStateModel>): Observable<void[]> {
    const state = ctx.getState();
    const dismissibleIds = state.pendingNotifications
      .filter(notification => notification.class === NotificationClass.NORMAL)
      .map(notification => notification.id);
    if (state.isLoading || dismissibleIds.length === 0) {
      return EMPTY;
    }

    ctx.patchState({ isLoading: true });
    return forkJoin(dismissibleIds.map(id => this.notificationsService.dismissNotification(id))).pipe(
      tap(() =>
        ctx.setState(currentState => ({
          ...currentState,
          pendingNotifications: currentState.pendingNotifications.filter(notification => notification.class !== NotificationClass.NORMAL),
        })),
      ),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }
}
